<template>
  <LoadingWrapper :state="status">
    <template v-if="team">
      <div class="flex flex-row flex-wrap items-center gap-2">
        <BaseTextButton icon="arrow_back" @click="emits('back')">{{
          $t('common.back')
        }}</BaseTextButton>
        <h2 class="text-title-large md:text-headline-large grow">{{ team.name }}</h2>
        <BaseTextButton size="M" color="tonal" icon="person_add" @click="openAddDialog">
          {{ $t('settings.organizationTeams.addMembers') }}
        </BaseTextButton>
        <BaseMenu :items="menuItems" />
      </div>

      <p v-if="team.members.length === 0" class="text-body-large text-on-surface-variant">
        {{ $t('settings.organizationTeams.membersEmpty') }}
      </p>
      <div v-else class="flex flex-col gap-4">
        <ul v-for="group in memberGroups" :key="group.id" class="flex flex-col gap-0.5">
          <li
            v-for="member in group.members"
            :key="member.id"
            class="bg-surface-container rounded-small first:rounded-t-large last:rounded-b-large flex flex-row items-center p-2 pr-1"
          >
            <BaseMemberAvatar :member="member" class="mr-4" />
            <div class="flex min-w-0 flex-col">
              <span class="text-body-large font-medium">{{ memberFullName(member) }}</span>
              <span v-if="secondaryLine(member)" class="text-body-medium text-on-surface-variant">{{
                secondaryLine(member)
              }}</span>
            </div>
            <div class="ml-auto flex shrink-0 items-center gap-1">
              <BaseChip v-if="!member.canLogin">{{
                $t('organization.loginLessMember.cannotSignIn')
              }}</BaseChip>
              <BaseIconButton
                v-tooltip="$t('settings.organizationTeams.removeMember')"
                icon="close"
                color="standard"
                size="S"
                @click="askRemoveMember(member)"
              />
            </div>
          </li>
        </ul>
      </div>
      <div v-if="canManagePortal" class="mt-8">
        <TeamPortalPanel :team-id="teamId" />
      </div>
    </template>

    <RenameTeamDialog
      ref="renameDialog"
      :is-open="isRenameOpen"
      :team-name="team?.name ?? ''"
      @close="closeRenameDialog"
      @rename="onRename"
    />
    <AddTeamMembersDialog
      :is-open="isAddOpen"
      :member-ids-in-team="memberIds"
      @close="closeAddDialog"
      @add="onAddMembers"
    />
    <BaseDialog
      type="basic"
      :is-open="isDeleteOpen"
      :title="$t('settings.organizationTeams.delete')"
      :description="
        $t('settings.organizationTeams.deleteDescription', {
          team: team?.name ?? '',
        })
      "
      class="md:w-100"
      @close="closeDeleteDialog"
    >
      <template #dismiss>
        <BaseTextButton @click="closeDeleteDialog">{{ $t('common.cancel') }}</BaseTextButton>
      </template>
      <template #confirm>
        <BaseTextButton @click="onDelete">{{
          $t('settings.organizationTeams.deleteConfirm')
        }}</BaseTextButton>
      </template>
    </BaseDialog>
    <BaseDialog
      type="basic"
      :is-open="isRemoveOpen"
      :title="$t('settings.organizationTeams.removeMember')"
      :description="
        $t('settings.organizationTeams.removeMemberDescription', {
          member: memberToRemove ? memberFullName(memberToRemove) : '',
        })
      "
      class="md:w-100"
      @close="closeRemoveDialog"
    >
      <template #dismiss>
        <BaseTextButton @click="closeRemoveDialog">{{ $t('common.cancel') }}</BaseTextButton>
      </template>
      <template #confirm>
        <BaseTextButton @click="onRemoveMember">{{
          $t('settings.organizationTeams.removeMember')
        }}</BaseTextButton>
      </template>
    </BaseDialog>
  </LoadingWrapper>
</template>
<script setup lang="ts">
  import { computed, ref, useTemplateRef } from 'vue'
  import { useI18n } from 'vue-i18n'
  import BaseChip from '@/core/components/base/chip/BaseChip.vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import BaseIconButton from '@/core/components/base/button/icon-button/BaseIconButton.vue'
  import BaseMemberAvatar from '@/core/components/base/avatar/BaseMemberAvatar.vue'
  import BaseMenu from '@/core/components/base/menu/BaseMenu.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'
  import type { MenuItemType } from '@/core/components/base/menu/MenuItem'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import type { Member } from '@/core/models/member/member'
  import { memberFullName, memberSecondaryInformation } from '@/core/models/member/member-name'
  import { usePermission } from '@/core/composables/use-permission'
  import { Permission } from '@/core/models/user'
  import TeamPortalPanel from '@/modules/portal/components/TeamPortalPanel.vue'
  import { useOrganizationTeam } from '../composables/use-organization-team'
  import { useOrganizationTeams } from '../composables/use-organization-teams'
  import AddTeamMembersDialog from './AddTeamMembersDialog.vue'
  import RenameTeamDialog from './RenameTeamDialog.vue'

  interface Props {
    teamId: string
  }

  const props = defineProps<Props>()

  const emits = defineEmits<{
    (e: 'back'): void
  }>()

  const { t } = useI18n()
  const { can } = usePermission()
  const canManagePortal = computed(() => can(Permission.PORTAL_MANAGE))
  const { team, status, renameTeam, addMembers, removeMember } = useOrganizationTeam(props.teamId)
  const { deleteTeam } = useOrganizationTeams()

  const memberIds = computed(() => team.value?.members.map((member) => member.id) ?? [])

  const memberGroups = computed(() => {
    const members = team.value?.members ?? []
    return [
      {
        id: 'login',
        members: members.filter((member) => !member.pending && member.canLogin),
      },
      {
        id: 'login-less',
        members: members.filter((member) => !member.pending && !member.canLogin),
      },
      {
        id: 'pending',
        members: members.filter((member) => member.pending),
      },
    ].filter((group) => group.members.length > 0)
  })

  const {
    isOpen: isRenameOpen,
    openDialog: openRenameDialog,
    closeDialog: closeRenameDialog,
  } = useDialog()
  const { isOpen: isAddOpen, openDialog: openAddDialog, closeDialog: closeAddDialog } = useDialog()
  const {
    isOpen: isDeleteOpen,
    openDialog: openDeleteDialog,
    closeDialog: closeDeleteDialog,
  } = useDialog()
  const {
    isOpen: isRemoveOpen,
    openDialog: openRemoveDialog,
    closeDialog: closeRemoveDialog,
  } = useDialog()

  const renameDialog = useTemplateRef('renameDialog')
  const memberToRemove = ref<Member | null>(null)

  const menuItems = computed<MenuItemType>(() => [
    {
      items: [
        {
          id: 'rename',
          title: t('settings.organizationTeams.rename'),
          leadingIcon: 'edit',
          callback: openRenameDialog,
        },
      ],
    },
    {
      items: [
        {
          id: 'delete',
          title: t('settings.organizationTeams.delete'),
          leadingIcon: 'delete',
          destructive: true,
          callback: openDeleteDialog,
        },
      ],
    },
  ])

  const secondaryLine = (member: Member) => {
    if (member.pending) {
      return t('settings.organizationTeams.pending')
    }
    return memberSecondaryInformation(member)
  }

  const onRename = async (name: string) => {
    const result = await renameTeam(name)
    if (result === 'invalid-name') {
      renameDialog.value?.showInvalidName()
      return
    }
    if (result) {
      closeRenameDialog()
    }
  }

  const onAddMembers = async (ids: string[]) => {
    const added = await addMembers(ids)
    if (added) {
      closeAddDialog()
    }
  }

  const onDelete = async () => {
    const deleted = await deleteTeam(props.teamId)
    if (deleted) {
      closeDeleteDialog()
      emits('back')
    }
  }

  const askRemoveMember = (member: Member) => {
    memberToRemove.value = member
    openRemoveDialog()
  }

  const onRemoveMember = async () => {
    if (!memberToRemove.value) return
    const removed = await removeMember(memberToRemove.value.id)
    if (removed) {
      closeRemoveDialog()
      memberToRemove.value = null
    }
  }
</script>
